import React from "react";
import styled from "styled-components";
import { SectionH2, GradientWord, Eyebrow } from "../components/Headlines";
import { MainBtn } from "../components/Btns";
import { FaCheck } from "react-icons/fa";
import { carePlans } from "../data";
import { useContact } from "../contexts/ContactContext";

const StyledCarePlans = styled.section`
  background-color: ${(props) => props.theme.darkBg2};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 100px 30px;

  @media screen and (min-width: 800px) {
    padding: 120px 60px;
  }
`;

const Header = styled.div`
  text-align: center;
  max-width: 720px;
  margin-bottom: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`;

const Subhead = styled.p`
  font-family: ${(props) => props.theme.lightFont};
  font-size: 18px;
  line-height: 1.6;
  color: ${(props) => props.theme.textMuted};
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  width: 100%;
  max-width: 1080px;

  @media screen and (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
    align-items: stretch;
  }
`;

const Plan = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px 28px;
  background: ${(props) => props.theme.panel};
  border: 1px solid
    ${(props) => (props.$featured ? props.theme.accentBorder : props.theme.hairline)};
  border-radius: 20px;
  position: relative;

  ${(props) =>
    props.$featured &&
    `box-shadow: 0 0 0 1px ${props.theme.accentBorder}, 0 20px 60px -30px ${props.theme.accent};`}
`;

const Badge = styled.span`
  position: absolute;
  top: -12px;
  left: 28px;
  font-family: ${(props) => props.theme.regularFont};
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: white;
  background: ${(props) => props.theme.accent};
  padding: 5px 12px;
  border-radius: 20px;
`;

const PlanName = styled.h3`
  font-family: ${(props) => props.theme.regularFont};
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
  margin: 0 0 6px 0;
`;

const Tagline = styled.p`
  font-family: ${(props) => props.theme.lightFont};
  font-size: 14px;
  line-height: 1.5;
  color: ${(props) => props.theme.textMuted};
  margin: 0 0 22px 0;
  min-height: 42px;
`;

const Hours = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 16px 0 22px;
  margin-bottom: 22px;
  border-bottom: 1px solid ${(props) => props.theme.hairline};
`;

const HoursNum = styled.span`
  font-family: ${(props) => props.theme.regularFont};
  font-size: 40px;
  font-weight: 600;
  line-height: 1;
  background: ${(props) => props.theme.brandGradient};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const HoursUnit = styled.span`
  font-family: ${(props) => props.theme.regularFont};
  font-size: 15px;
  color: ${(props) => props.theme.textColor};
  display: flex;
  flex-direction: column;
  line-height: 1.25;
`;

const HoursNote = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.textMuted};
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 28px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-grow: 1;
`;

const Feature = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-family: ${(props) => props.theme.lightFont};
  font-size: 15px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.85);

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    margin-top: 4px;
    color: ${(props) => props.theme.accent};
  }
`;

const PlanCta = styled(MainBtn)`
  width: 100%;
`;

const Footnote = styled.p`
  font-family: ${(props) => props.theme.lightFont};
  font-size: 14px;
  color: ${(props) => props.theme.textMuted};
  margin: 36px 0 0 0;
  text-align: center;
`;

export default function CarePlans() {
  const { openContact } = useContact();
  return (
    <StyledCarePlans id="pricing">
      <Header>
        <Eyebrow>Care plans</Eyebrow>
        <SectionH2>
          Maintenance you can <GradientWord>see</GradientWord>
        </SectionH2>
        <Subhead>
          Most agencies make you "request a quote." We don't. Pick the level of
          care your site needs - clear scope, real SLA, no surprises.
        </Subhead>
      </Header>
      <Grid>
        {carePlans.map((plan) => (
          <Plan key={plan.id} $featured={plan.featured}>
            {plan.featured && <Badge>Most popular</Badge>}
            <PlanName>{plan.name}</PlanName>
            <Tagline>{plan.tagline}</Tagline>
            <Hours>
              <HoursNum>{plan.hours}</HoursNum>
              <HoursUnit>
                hrs / mo
                <HoursNote>content, design or dev</HoursNote>
              </HoursUnit>
            </Hours>
            <FeatureList>
              {plan.features.map((f) => (
                <Feature key={f}>
                  <FaCheck />
                  <span>{f}</span>
                </Feature>
              ))}
            </FeatureList>
            <PlanCta onClick={() => openContact(plan.name)}>Let's talk</PlanCta>
          </Plan>
        ))}
      </Grid>
      <Footnote>
        New site? We design and build it first - then choose the plan that keeps
        it sharp.
      </Footnote>
    </StyledCarePlans>
  );
}
